import { useState } from 'react';
import { usePrivy } from '@privy-io/react-auth';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/app/components/ui/dialog';
import { Vote, Clock, Check, Loader2, TrendingUp } from 'lucide-react';
import { useVotingCharacters, useSubmitVote, type VotingCharacter } from '@/api';

const CATEGORY_MAP: Record<string, string> = {
  'screen-legends': 'Screen Legends',
  'toons': 'Toons',
  'irl-vibes': 'IRL Vibes',
  'memeverse': 'Memeverse',
  'original-chaos': 'Original Chaos',
};

export function VotingPage() {
  const { authenticated: isConnected } = usePrivy();
  const { data, isLoading, error } = useVotingCharacters();
  const submitVote = useSubmitVote();

  const [selectedCharacter, setSelectedCharacter] = useState<VotingCharacter | null>(null);
  const [selectedVote, setSelectedVote] = useState<'yes' | 'no' | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const characters = data?.data?.characters ?? [];
  const votingEnabled = data?.data?.votingEnabled ?? false;

  const handleVoteClick = (character: VotingCharacter, vote: 'yes' | 'no') => {
    if (character.userVote || !isConnected || !votingEnabled) return;
    setSelectedCharacter(character);
    setSelectedVote(vote);
    setShowConfirmModal(true);
  };

  const confirmVote = async () => {
    if (!selectedCharacter || !selectedVote) return;

    try {
      await submitVote.mutateAsync({
        characterId: selectedCharacter._id,
        vote: selectedVote,
      });
      setShowConfirmModal(false);
      setSelectedCharacter(null);
      setSelectedVote(null);
    } catch (err) {
      console.error('Failed to submit vote:', err);
    }
  };

  const totalVotesForCharacter = (c: VotingCharacter) => c.yesVotes + c.noVotes;
  const maxVotes = Math.max(...characters.map(totalVotesForCharacter), 1);

  if (!isConnected) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-12 text-center">
        <h1 className="font-['Poppins',sans-serif] text-4xl font-semibold text-white mb-3">
          Community Voting
        </h1>
        <p className="text-lg text-[#9595a5] mb-8">
          Connect your wallet to vote on characters
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-12 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-10 h-10 text-white animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-12 text-center">
        <p className="text-[#9595a5]">Failed to load voting. Please try again.</p>
      </div>
    );
  }

  if (characters.length === 0) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-12">
        <h1 className="font-['Poppins',sans-serif] text-4xl font-semibold text-white mb-3">
          Community Voting
        </h1>
        <p className="text-lg text-[#9595a5] mb-8">
          No characters in voting right now. Check back later!
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      {/* Header */}
      <div className="mb-12 flex items-center justify-between">
        <div>
          <h1 className="font-['Poppins',sans-serif] text-4xl font-semibold text-white mb-3">
            Community Voting
          </h1>
          <p className="text-lg text-[#9595a5]">
            Vote on characters using blockchain signatures
          </p>
        </div>
        {!votingEnabled && (
          <div className="px-4 py-2 rounded-lg bg-white/10 border border-white/30 text-sm text-white">
            Voting paused
          </div>
        )}
      </div>

      {/* Character Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {characters.map((character) => {
          const totalVotes = totalVotesForCharacter(character);
          const percentage = totalVotes > 0 ? ((character.yesVotes / totalVotes) * 100).toFixed(1) : '0';
          const isLeading = totalVotes > 0 && character.yesVotes === Math.max(...characters.map((c) => c.yesVotes));
          const hasVoted = !!character.userVote;
          const isSelectedYes = character.userVote === 'yes';
          const isSelectedNo = character.userVote === 'no';

          return (
            <div
              key={character._id}
              className={`relative p-6 rounded-xl border-2 transition-all ${
                hasVoted ? 'opacity-90' : 'border-white/[0.08]'
              } bg-[#18181f] ${!isConnected && 'opacity-50'}`}
            >
              {isLeading && totalVotes > 0 && (
                <div className="absolute top-4 right-4">
                  <div className="px-2 py-1 rounded-md bg-white/20 text-white text-xs font-medium flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" />
                    Leading
                  </div>
                </div>
              )}

              {hasVoted && (
                <div className="absolute top-4 right-4">
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                    <Check className="w-5 h-5 text-white" />
                  </div>
                </div>
              )}

              <div className="flex items-start gap-4 mb-4">
                <div className="w-16 h-16 rounded-xl bg-[#2a2a35] flex items-center justify-center overflow-hidden flex-shrink-0">
                  {character.imageUrl ? (
                    <img src={character.imageUrl} alt={character.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-3xl">🤖</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-['Poppins',sans-serif] text-lg font-semibold text-white mb-1">
                    {character.name}
                  </h4>
                  <p className="text-sm text-[#9595a5] line-clamp-2">{character.description}</p>
                  <span className="inline-block mt-2 text-xs text-[#9595a5]">
                    {CATEGORY_MAP[character.category] || character.category}
                  </span>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#9595a5]">
                    Yes: {character.yesVotes} · No: {character.noVotes}
                  </span>
                  {totalVotes > 0 && (
                    <span className="text-white font-medium">{percentage}% yes</span>
                  )}
                </div>
                <div className="w-full h-2 rounded-full bg-[#2a2a35] overflow-hidden">
                  <div
                    className="h-full bg-white transition-all duration-500"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => handleVoteClick(character, 'yes')}
                  disabled={hasVoted || !isConnected || !votingEnabled}
                  className={`flex-1 py-2.5 rounded-lg font-medium transition-all ${
                    hasVoted || !isConnected || !votingEnabled
                      ? 'bg-white/10 text-white/40 cursor-not-allowed'
                      : isSelectedYes
                      ? 'bg-white text-black'
                      : 'bg-white text-black hover:opacity-90'
                  }`}
                >
                  Yes
                </button>
                <button
                  onClick={() => handleVoteClick(character, 'no')}
                  disabled={hasVoted || !isConnected || !votingEnabled}
                  className={`flex-1 py-2.5 rounded-lg font-medium transition-all ${
                    hasVoted || !isConnected || !votingEnabled
                      ? 'bg-white/10 text-white/40 cursor-not-allowed'
                      : isSelectedNo
                      ? 'border-2 border-white text-white'
                      : 'border-2 border-white/[0.08] text-white hover:border-white/[0.15] hover:bg-white/5'
                  }`}
                >
                  No
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Vote Confirmation Modal */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="bg-[#18181f] border-white/[0.08] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Poppins',sans-serif] text-xl">
              {submitVote.isPending ? 'Submitting Vote...' : 'Confirm Your Vote'}
            </DialogTitle>
            <DialogDescription className="text-[#9595a5]">
              {submitVote.isPending
                ? 'Please wait while we record your vote.'
                : 'Review your vote before submitting.'}
            </DialogDescription>
          </DialogHeader>

          {submitVote.isPending ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="w-12 h-12 text-white animate-spin mb-4" />
              <p className="text-sm text-[#9595a5]">Recording your vote...</p>
            </div>
          ) : selectedCharacter && selectedVote ? (
            <div className="space-y-4">
              <p className="text-sm text-[#9595a5]">
                Voting <span className="text-white font-medium">{selectedVote}</span> for{' '}
                <span className="text-white font-medium">{selectedCharacter.name}</span>
              </p>
              <div className="p-4 rounded-lg bg-white/10 border border-white/30 text-sm text-white">
                Your vote will be recorded and cannot be changed
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={() => setShowConfirmModal(false)}
                  variant="outline"
                  className="flex-1 bg-transparent border-white/[0.08] text-white hover:bg-[#2a2a35]/50"
                >
                  Cancel
                </Button>
                <Button onClick={confirmVote} className="flex-1 bg-white hover:opacity-90">
                  <Vote className="w-4 h-4 mr-2" />
                  Confirm Vote
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
